// Spreadsheet-style preview and editor for staged import rows.
//
// Replaces the MUI <Table> version in ChatAssistant, which rendered roughly fifteen
// <Select>/<TextField> components per row and therefore needed pagination just to stay
// responsive. Handsontable renders virtualised cells, so the whole file is visible and
// editable at once, and paste-from-Excel comes for free.
//
// OWNERSHIP OF THE DATA - this matters.
// Handsontable owns the row data once loaded; we never feed edited values back in via
// the `data` prop. Passing a fresh array on every keystroke makes the wrapper call
// updateSettings({data}), which reloads the table and throws away the user's selection
// and scroll position mid-edit. Same approach as EditBudgetsTable: hand it the initial
// data, let it mutate internally, and reach in through the ref for bulk edits.
//
// The parent is still told about every edit (afterChange) so validation stays live -
// it just must not round-trip those values back into `data`. Validation results reach
// the grid through a ref that the Status renderer reads, so re-validating repaints
// cells without reloading anything.
import HotTable from '@handsontable/react-wrapper';
import { ColumnSettings } from 'handsontable/settings';
import { Box } from '@mui/material';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { months } from '../../../../Helpers';
import type { ImportFieldDescriptor } from './ImportModelRegistry';
import { ImportContext, ImportRow, ValidatedImportRow, getEntityLabel } from './ImportRow';

// Same key the rest of the app uses (see EditBudgetsTable / UpdatePositions).
const HANDSONTABLE_LICENSE_KEY = '29ae0-f35ad-79c41-2472d-2c70d';

const ROW_NUMBER_COLUMN = '__rowNumber';
const STATUS_COLUMN = '__status';

export interface ImportReviewGridRef {
    /** Write one value into many rows without reloading the table. */
    applyBulkEdit: (rowIds: string[], fieldKey: string, value: unknown) => void;
}

export interface ImportReviewGridProps {
    fields: ImportFieldDescriptor[];
    /**
     * The rows as parsed from the FILE. Must stay referentially stable across edits -
     * pass the pre-edit rows, not ones with edits merged in.
     */
    rows: ImportRow[];
    /** Latest validation results, keyed by row id. Read through a ref by the renderer. */
    validations: Map<string, ValidatedImportRow>;
    ctx: ImportContext;
    onChangeCell: (rowId: string, fieldKey: string, value: unknown) => void;
    onSelectionChange?: (selectedRowIds: string[]) => void;
    readOnly?: boolean;
    height?: number | string;
}

function entityOptions(field: ImportFieldDescriptor, ctx: ImportContext): string[] {
    const source = field.entitySource;
    if (!source) return [];
    const collection =
        source === 'budgetPricingStrategies' ? (ctx.pricingStrategies ?? [])
        : source === 'budgetRounds' ? (ctx.customerData?.budgetRounds ?? [])
        : source === 'commodities' ? (ctx.customerData?.commodities ?? [])
        : source === 'counterparties' ? (ctx.customerData?.counterparties ?? [])
        : source === 'locations' ? (ctx.customerData?.locations ?? [])
        : (ctx.customerData?.portfolios ?? []);

    return collection
        .map(entity => getEntityLabel(source, entity))
        .filter(label => label.length > 0);
}

const ImportReviewGrid = forwardRef<ImportReviewGridRef, ImportReviewGridProps>((props, ref) => {
    const {
        fields, rows, validations, ctx, onChangeCell, onSelectionChange,
        readOnly = false, height = 460,
    } = props;

    const hotRef = useRef<any>(null);

    // Kept in a ref rather than a dependency so the Status renderer always sees the
    // latest validation without the data array having to change identity.
    const validationsRef = useRef(validations);
    validationsRef.current = validations;

    const rowIds = useMemo(() => rows.map(r => r.id), [rows]);
    const rowIndexById = useMemo(() => {
        const index = new Map<string, number>();
        rowIds.forEach((id, i) => index.set(id, i));
        return index;
    }, [rowIds]);

    // Depends ONLY on rows. Edited values deliberately do not appear here.
    const data = useMemo(
        () => rows.map(row => ({
            ...row.values,
            [ROW_NUMBER_COLUMN]: row.rowNumber,
            [STATUS_COLUMN]: '',
        })),
        [rows]
    );

    useImperativeHandle(ref, () => ({
        applyBulkEdit: (targetRowIds: string[], fieldKey: string, value: unknown) => {
            const instance = hotRef.current?.hotInstance;
            if (!instance) return;
            const changes: [number, string, unknown][] = [];
            for (const rowId of targetRowIds) {
                const rowIndex = rowIndexById.get(rowId);
                if (rowIndex === undefined) continue;
                changes.push([rowIndex, fieldKey, value]);
            }
            if (changes.length > 0) instance.setDataAtRowProp(changes, 'bulkEdit');
        },
    }), [rowIndexById]);

    // Paints the Status cell from the live validation map.
    const statusRenderer = useCallback((
        _instance: any, td: HTMLElement, row: number
    ) => {
        const rowId = rowIds[row];
        const validation = rowId ? validationsRef.current.get(rowId) : undefined;
        const errors = validation?.errors ?? [];
        const warnings = validation?.warnings ?? [];

        td.className = '';
        if (errors.length > 0) {
            // The message itself, not just a count: a whole file usually fails the same
            // way, and "1 error" sends the user hunting through a hover tooltip to find
            // out which column is wrong.
            td.textContent = errors.length === 1
                ? errors[0].message
                : `${errors.length} errors: ${errors[0].message}`;
            td.className = 'import-cell-error';
            td.title = errors.map(e => e.message).join('\n');
        } else if (warnings.length > 0) {
            td.textContent = warnings.length === 1
                ? warnings[0].message
                : `${warnings.length} warnings: ${warnings[0].message}`;
            td.className = 'import-cell-warning';
            td.title = warnings.map(w => w.message).join('\n');
        } else {
            td.textContent = 'OK';
            td.title = '';
        }
        return td;
    }, [rowIds]);

    const columns = useMemo<ColumnSettings[]>(() => {
        const leading: ColumnSettings[] = [
            { data: ROW_NUMBER_COLUMN, type: 'numeric', readOnly: true, width: 60 },
            { data: STATUS_COLUMN, readOnly: true, width: 260, renderer: statusRenderer },
        ];

        const fieldColumns = fields.map<ColumnSettings>(field => {
            const base: ColumnSettings = { data: field.key, readOnly };

            switch (field.kind) {
                case 'entity':
                    return {
                        ...base,
                        type: 'dropdown',
                        source: entityOptions(field, ctx),
                        // Let an unknown value through so it surfaces as a validation
                        // error with an explanation, rather than the editor silently
                        // refusing it.
                        allowInvalid: true,
                        width: 150,
                    };
                case 'enum':
                    return {
                        ...base,
                        type: 'dropdown',
                        source: field.enumValues ? Object.values(field.enumValues) : [],
                        allowInvalid: true,
                        width: 120,
                    };
                case 'month':
                    return { ...base, type: 'dropdown', source: [...months], allowInvalid: true, width: 110 };
                case 'year':
                    return { ...base, type: 'numeric', numericFormat: { pattern: '0' }, width: 90 };
                case 'number':
                    return { ...base, type: 'numeric', numericFormat: { pattern: '0,0.[0000]' }, width: 110 };
                case 'date':
                    return {
                        ...base,
                        type: 'date',
                        dateFormat: 'YYYY-MM-DD',
                        correctFormat: true,
                        allowInvalid: true,
                        width: 120,
                    };
                default:
                    return { ...base, type: 'text', width: 140 };
            }
        });

        return [...leading, ...fieldColumns];
    }, [fields, ctx, readOnly, statusRenderer]);

    const colHeaders = useMemo(
        () => ['Row', 'Status', ...fields.map(f => (f.required ? `${f.label} *` : f.label))],
        [fields]
    );

    // Highlight the specific cells that failed, not just the row.
    const cells = useCallback((rowIndex: number, _colIndex: number, prop: string | number) => {
        const rowId = rowIds[rowIndex];
        const validation = rowId ? validationsRef.current.get(rowId) : undefined;
        if (!validation) return {};

        const fieldKey = String(prop);
        if (validation.errors.some(issue => issue.field === fieldKey)) {
            return { className: 'import-cell-error' };
        }
        if (validation.warnings.some(issue => issue.field === fieldKey)) {
            return { className: 'import-cell-warning' };
        }
        return {};
    }, [rowIds]);

    const afterChange = useCallback((changes: any[] | null, source: string) => {
        // loadData fires when the table first receives data; only react to real edits.
        if (!changes || source === 'loadData') return;

        for (const [rowIndex, prop, oldValue, newValue] of changes) {
            if (oldValue === newValue) continue;
            const fieldKey = String(prop);
            if (fieldKey === ROW_NUMBER_COLUMN || fieldKey === STATUS_COLUMN) continue;
            const rowId = rowIds[rowIndex];
            if (!rowId) continue;
            onChangeCell(rowId, fieldKey, newValue);
        }
    }, [rowIds, onChangeCell]);

    const afterSelection = useCallback(() => {
        if (!onSelectionChange) return;
        const instance = hotRef.current?.hotInstance;
        if (!instance) return;

        const selected: number[][] = instance.getSelected() ?? [];
        const ids = new Set<string>();
        for (const [startRow, , endRow] of selected) {
            const from = Math.min(startRow, endRow);
            const to = Math.max(startRow, endRow);
            for (let i = from; i <= to; i++) {
                const id = rowIds[i];
                if (id) ids.add(id);
            }
        }
        onSelectionChange(Array.from(ids));
    }, [rowIds, onSelectionChange]);

    return (
        <Box sx={{
            width: '100%',
            '& .import-cell-error': { backgroundColor: '#fdecea' },
            '& .import-cell-warning': { backgroundColor: '#fff4e5' },
        }}>
            <HotTable
                ref={hotRef}
                data={data}
                columns={columns}
                colHeaders={colHeaders}
                rowHeaders={false}
                width="100%"
                height={height}
                stretchH="last"
                autoWrapRow
                autoWrapCol
                rowHeights={32}
                columnHeaderHeight={40}
                minSpareRows={0}
                manualColumnResize
                cells={cells}
                afterChange={afterChange}
                afterSelectionEnd={afterSelection}
                licenseKey={HANDSONTABLE_LICENSE_KEY}
            />
        </Box>
    );
});

ImportReviewGrid.displayName = 'ImportReviewGrid';

export default ImportReviewGrid;
